/**
 * 选座面板。两种用法共用一张：
 *   - 坐下（`sheet === 'seatPicker'`）：旁观者点「选个座位坐下 / 坐下」后从这里挑位置，发 `sitDown`；
 *   - 换座（`sheet === 'seatChange'`，从「我的座位」面板进来）：空座发 `changeSeat`。
 *     已经坐着的人不管从哪条路进来都是换座 —— 以前这里只写一句「你在 N 号位」，是个死胡同。
 *
 * ★ 红线 #38：`sitDown` / `changeSeat` 只带座位号，**没有买入金额**——筹码由服务端决定
 *   （新坐下取起始筹码，回来的人领回托管的筹码，换座筹码跟人走）。
 *   以前标题旁写着「买入 {startingStack}」：对领回托管筹码的人是错的，买入也已经在房间面板里写着，删了。
 * ★ 红线 #37：`changeSeat` 只说「想去哪」，自己现在的座位由连接身份解析。
 *
 * 用 `uiStore.sheet` 控制开合，而不是自己拿一个局部 state：
 * 这样「打开选座」这件事和设置面板、历史面板走同一条互斥通道，
 * 不会出现两层 sheet 叠在一起、背后的牌桌被盖死两次。
 */
import { useCallback } from 'react'
import type { ReactNode } from 'react'
import type { PlayerView, SeatIndex } from '@poker/shared'

import { useModalDialog } from '../hooks/useModalDialog.js'
import { useOpenTapGuard } from '../hooks/useOpenTapGuard.js'
import { useUiStore } from '../store/index.js'
import { selectNetView, useNetStore } from '../transport/applyMessage.js'

import { SeatList } from './SeatList.js'
import { emptySeatIndexes, heroSeatOf, seatPickMessage, seatSelfActions } from './roomDerive.js'
import { useRoomLink } from './roomLink.js'
import './room.css'

export type SeatPickerMode = 'sit' | 'change'

export function SitDownSheet(): ReactNode {
  const sheet = useUiStore((s) => s.sheet)
  const closeSheet = useUiStore((s) => s.closeSheet)
  const view = useNetStore(selectNetView)
  const link = useRoomLink()
  const open = (sheet === 'seatPicker' || sheet === 'seatChange') && view !== null
  const mode: SeatPickerMode = sheet === 'seatChange' || heroSeatOf(view) !== null ? 'change' : 'sit'

  const pick = useCallback(
    (seat: SeatIndex): void => {
      link.send(seatPickMessage(mode, seat))
      // 乐观关闭：座位被抢了会回 E_SEAT_TAKEN，届时由 Toast 说明，
      // 而不是让面板一直挂着等回执——那会让「点了没反应」成为常态观感。
      closeSheet()
    },
    [link, mode, closeSheet],
  )

  if (!open) return null
  return <SeatPickerPanel view={view} mode={mode} onPick={pick} onClose={closeSheet} />
}

export interface SeatPickerPanelProps {
  readonly view: PlayerView
  readonly mode: SeatPickerMode
  readonly onPick: (seat: SeatIndex) => void
  readonly onClose: () => void
}

/**
 * 面板本体，只吃 props（node 测试里挂在 store 上的开合层静态渲染不出来）。
 */
export function SeatPickerPanel({ view, mode, onPick, onClose }: SeatPickerPanelProps): ReactNode {
  const { dialogRef, onDialogKeyDown } = useModalDialog({ open: true, onClose })
  // 换座面板是从「我的座位」的「换座」那一行上打开的：双击那一行，第二下不许落到某个「换过去」上
  const guardTap = useOpenTapGuard(true)
  const free = emptySeatIndexes(view)
  const hero = heroSeatOf(view)
  const change = mode === 'change'
  // 换座不了的原因与「我的座位」面板那一行同一句（本手发到了牌 / 买分或转账在途 / 没空位）
  const blocked = change ? (seatSelfActions(view)?.changeBlocked ?? null) : null

  return (
    <div className="sheet-scrim sheet-scrim--paper" onClickCapture={guardTap} onClick={onClose} role="presentation">
      <div
        className="sheet"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={change ? '换座' : '选择座位'}
        tabIndex={-1}
        onKeyDown={onDialogKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sheet__head">
          <strong>{change ? '换座' : '选择座位'}</strong>
          {change && hero !== null && <span className="sheet__sub num">现在 {hero + 1} 号位</span>}
        </header>

        {blocked !== null ? (
          <p className="sheet__empty">{blocked}</p>
        ) : free.length === 0 ? (
          <p className="sheet__empty">座位都满了，等有人离开</p>
        ) : (
          <SeatList
            seats={view.seats}
            hand={view.hand}
            heroSeat={hero}
            canSit
            onSit={onPick}
            sitLabel={change ? '换过去' : '坐下'}
            topUps={view.topUps ?? []}
          />
        )}

        <button type="button" className="btn btn--ghost sheet__close" onClick={onClose}>
          关闭
        </button>
      </div>
    </div>
  )
}

export default SitDownSheet
